import { Component, ViewEncapsulation, inject } from '@angular/core';
import { UserManagementService } from '../../../services/user-management.service';
import { ToastService } from '@core/services/toast.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Role } from '@core/enums/roles';
import { IDistrict, IRoles } from '@core/interfaces/ilookup';
import { UserValidator } from '@core/validators/user.validator';
import { LookupService } from '@core/services/lookup.service';
import { take, tap } from 'rxjs';
import { AuthService } from '@core/services/auth.service';
import { NameValidator } from '@core/validators/name.validator';
import { Router } from '@angular/router';
import { ISchool } from '@views/features/management/interfaces/isearchSchool';
import { CustomValidators } from '@core/validators/field.validator';

@Component({
  selector: 'prism-user-form',
  templateUrl: './user-form.component.html',
  styleUrl: './user-form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class UserFormComponent {
  userManagementService = inject(UserManagementService);
  private lookupService = inject(LookupService);
  private toastService= inject(ToastService);
  private userValidator = inject(UserValidator);
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private nameValidator = inject(NameValidator);
  private router = inject(Router);

  user = this.authService.getCurrentUser();
  isDistrictAdmin = this.user.roleId == Role.DistrictAdmin;
  isUser = this.user.roleId == Role.User;
  userForm!: FormGroup;
  roles = Role;
  roleOptions: IRoles[] = [];
  organizationOptions: IDistrict[] = [];
  initialEmail: string = '';
  schoolOptions: ISchool[] = [];
  arrList: number[] = [];

  get firstName() {
    return this.userForm.get('firstName');
  }
  get lastName() {
    return this.userForm.get('lastName');
  }
  get emailName() {
    return this.userForm.get('emailName');
  }
  get roleId() {
    return this.userForm.get('roleId');
  }
  get gradeIds() {
    return this.userForm.get('gradeIds');
  }
  get districtId() {
    return this.userForm.get('districtIds');
  }
  get schoolId() {
    return this.userForm.get('schoolId');
  }

  ngOnInit(): void {
    this.initializeForm();
    this.listenUserType();
    this.listenOrganization();
  }

  initializeForm(): void {
    this.userForm = this.fb.group({
      id: [0],
      firstName: [null, [Validators.required, Validators.maxLength(50), this.nameValidator.noNumeric()]],
      lastName: [null, [Validators.required, Validators.maxLength(50), this.nameValidator.noNumeric()]],
      gradeIds: [[], Validators.required],
      emailName: [null, [Validators.required, Validators.email, CustomValidators.noWhitespace(), CustomValidators.blacklistEmail()]],
      roleId: [null, Validators.required],
      districtIds: [[], Validators.required],
      schoolId: [this.isDistrictAdmin ? this.user.schoolId : null, Validators.required],
    },
    { validator:
      [
        this.userValidator.validateEmailAddress('emailName', this.initialEmail)
      ]
    });
  }

  listenUserType(): void {
    this.roleId?.valueChanges.subscribe((roleId: number) => {
      if (roleId === Role.SystemAdmin) {
        this.districtId?.clearValidators();
        this.schoolId?.clearValidators();
        this.gradeIds?.clearValidators();
        this.schoolId?.setValue(null);
      } 
      else if (roleId === Role.DistrictAdmin) {
        this.schoolId?.clearValidators();
        this.gradeIds?.clearValidators();
        this.gradeIds?.setValue(null);
        this.schoolId?.setValue(null);
      }
      else {
        this.districtId?.setValidators([Validators.required]);
        if (roleId === Role.User) {
          this.schoolId?.setValidators([Validators.required]);
          this.gradeIds?.setValidators([Validators.required]);
          this.districtId?.setValue(this.districtId?.value?.[0] ? [this.districtId?.value[0]] : []);
        } else {
          this.schoolId?.clearValidators();
          this.schoolId?.setValue(null);
        }
      }
  
      this.districtId?.updateValueAndValidity();
      this.schoolId?.updateValueAndValidity();
      this.gradeIds?.updateValueAndValidity();
    });
  }

  listenOrganization(): void {
    this.districtId?.valueChanges.subscribe((districtIds: number[] | number) => {
      if (Array.isArray(districtIds)) {
        this.arrList = districtIds;
      } else if (typeof districtIds === 'number') {
        this.arrList = [districtIds];
      } else {
        this.arrList = [];
      }

      if (Array.isArray(districtIds) && districtIds != null) {
      this.districtId?.setValue(this.arrList, { emitEvent: false });
      }

      if (this.roleId?.value === Role.User && this.arrList.length) {
        this.schoolId?.setValue(null);
        this.getSchoolsByDistrict(this.arrList[0]);
      } else if (districtIds) {
        this.schoolId?.setValue(null);
      }
    });
  }

  getSchoolsByDistrict(districtId: number): void {
    this.lookupService.getSchoolsByDistrict(districtId).pipe(
      take(1),
      tap((schools: ISchool[]) => {
        this.schoolOptions = schools;
      })
    ).subscribe();
  }

  saveUser(): void {
    if (this.userForm.invalid) {
      Object.keys(this.userForm.controls).forEach((controlName) => {
        const control = this.userForm.get(controlName);
        if (controlName === 'schoolId' && (this.roleId?.value !== this.roles.User)) {
          this.schoolId?.markAsUntouched();
        } else if (control) {
          control.markAsTouched();
        }
      });
  
      this.toastService.error('Please fill out all required fields');
      return;
    }
  
    const payload = {
      ...this.userForm.value,
      districtIds: this.districtId?.value ? this.arrList : [],
    };
  
    if (payload.id == null) payload.id = 0;
  
    this.userManagementService.addUser(payload).pipe(
      take(1),
      tap((res: boolean) => {
        if (res) {
          this.toastService.success('User added successfully');
          this.userForm.reset();
          if (this.isDistrictAdmin) this.districtId?.setValue(this.user.districtId);
          this.router.navigate(['/user-management']);
        }
      })
    ).subscribe();
  }
}
